import { getBaseRole } from '@/helpers/customRoles';
import { FC, useEffect, useMemo, useState } from 'react'
import { Empty, Spin } from 'antd'
import classNames from 'classnames'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import Preloader from '@UI/Preloader/Preloader'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import { IcoRemove } from '@/utils/icons'
import { useAddAlert } from '@/hooks/useAddAlert'
import NotificationSettingsCard from './components/NotificationSettingsCard/NotificationSettingsCard'
import {
  getUserNotificationsSettings,
  updateUserParentNotification,
} from './utils/api'
import { NotificationAccess, UserNotificationSettings } from './utils/types'
import cn from './index.module.css'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import UserCard from '@pages/Admin/Manage/tabs/ClassesPage/components/ShowUsersModal/components/UserCard/UserCard'
import CheckboxLabel from '@pages/Admin/CourseSettings/components/CheckboxLabel/CheckboxLabel'
import { LoadingOutlined } from '@ant-design/icons'

interface INotificationsSettings {
  open: boolean
  userId?: number | null
  onDismiss: () => void
}

type AccessUser = IUser & Record<string, any>

const getIds = (value: unknown, keys: string[] = ['id', 'value']): string[] => {
  if (!value) return []

  if (Array.isArray(value)) {
    return value.flatMap((item) => getIds(item, keys))
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)]
  }

  if (typeof value === 'object') {
    const item = value as Record<string, any>

    return keys
      .flatMap((key) => getIds(item[key]))
      .filter((id, index, array) => array.indexOf(id) === index)
  }

  return []
}

const getArray = (value: unknown) => (Array.isArray(value) ? value : [])

const hasIntersection = (
  accessIds: Array<number | string> | undefined,
  userIds: string[],
) => {
  if (!accessIds?.length) return false

  const userIdSet = new Set(userIds.map(String))

  return accessIds.map(String).some((id) => userIdSet.has(id))
}

const getUserRoles = (user: AccessUser) =>
  [user.role, ...(Array.isArray(user.roles) ? user.roles : [])]
    .filter(Boolean)
    .map(String)

const getUserDepartmentIds = (user: AccessUser) =>
  [
    ...getIds(user.departments, ['id', 'department_id', 'value']),
    ...getIds(user.departmentIds),
    ...getIds(user.units, ['id', 'unit_id', 'value']),
  ]

const getUserTeamIds = (user: AccessUser) => [
  ...getIds(user.teams, ['id', 'team_id', 'value']),
  ...getIds(user.teamIds),
]

const getUserClassIds = (user: AccessUser) => [
  ...getIds(user.classes, ['id', 'class_id', 'value']),
  ...getIds(user.classIds),
  ...getIds(user.classUser, ['class_id', 'classId']),
  ...getIds(getArray(user.classUser).map((item: any) => item?.class)),
  ...getIds(user.orders, ['class_id', 'classId']),
  ...getIds(getArray(user.orders).map((item: any) => item?.class)),
]

const getUserDirectionIds = (user: AccessUser) => [
  ...getIds(user.directionIds),
  ...getIds(user.directions, ['id', 'direction_id', 'value']),
  ...getIds(user.orders, ['direction_id', 'directionId']),
  ...getIds(getArray(user.orders).map((item: any) => item?.direction)),
  ...getIds(user.directionOrders, ['direction_id', 'directionId']),
  ...getIds(getArray(user.directionOrders).map((item: any) => item?.direction)),
]

const hasAccessSettings = (access?: NotificationAccess | null) =>
  Boolean(
    access &&
      Object.values(access).some((value) => Array.isArray(value) && value.length),
  )

const isNotificationAvailableForUser = (
  notification: UserNotificationSettings['notifications'][number],
  user?: IUser | null,
) => {
  const access = notification.access ?? notification.trigger?.access

  if (!hasAccessSettings(access)) return true
  if (!user) return false

  const accessUser = user as AccessUser
  const userRoles = getUserRoles(accessUser)

  return (
    hasIntersection(access?.roles, userRoles) ||
    hasIntersection(access?.departments, getUserDepartmentIds(accessUser)) ||
    hasIntersection(access?.teams, getUserTeamIds(accessUser)) ||
    hasIntersection(access?.classes, getUserClassIds(accessUser)) ||
    hasIntersection(access?.directions, getUserDirectionIds(accessUser))
  )
}

const NotificationsSettings: FC<INotificationsSettings> = (props) => {
  return (
    <Modal
      active={props.open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={() => props.onDismiss()}
    >
      {props.open && (
        <ModalBody
          open={props.open}
          userId={props.userId}
          onDismiss={props.onDismiss}
        />
      )}
    </Modal>
  )
}

const ModalBody: FC<{
  open: boolean
  userId?: number | null
  onDismiss: () => void
}> = ({ userId, onDismiss }) => {
  const user = useSelector(selectProfile)
  const currentUserId = userId ?? user.id

  const addAlert = useAddAlert()
  const [form, setForm] = useState<UserNotificationSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [parentNotificationLoading, setParentNotificationLoading] =
    useState(false)
  const showUserCard = currentUserId !== form?.user?.id && !!form?.user
  const availableNotifications = useMemo(
    () =>
      form?.notifications?.filter((notification) =>
        isNotificationAvailableForUser(notification, form.user),
      ) ?? [],
    [form?.notifications, form?.user],
  )

  const loadNotifications = async () => {
    if (loading) return

    setLoading(true)
    const response = await getUserNotificationsSettings(currentUserId)
    setForm(response?.data ?? null)
    setLoading(false)

    if (!response.success) {
      addAlert({
        title: 'Ошибка',
        text: response.error || 'Не удалось загрузить настройки уведомлений',
        type: 'error',
      })
      return
    }
  }

  useEffect(() => {
    loadNotifications()
  }, [userId])

  const handleParentNotificationChange = async (value: boolean) => {
    if (parentNotificationLoading || !form) return

    setParentNotificationLoading(true)
    const response = await updateUserParentNotification(currentUserId, value)
    setParentNotificationLoading(false)

    if (response.success) {
      addAlert({
        title: 'Успех',
        text: 'Настройка уведомлений родителю успешно обновлена',
        type: 'success',
      })
    } else {
      addAlert({
        title: 'Ошибка',
        text:
          response.error ||
          'Не удалось обновить настройку уведомлений родителю',
        type: 'error',
      })
      return
    }

    setForm((prev) =>
      prev ? { ...prev, isParentNotificationEnabled: value } : prev,
    )
  }

  return (
    <div className={classNames(mclasses.modalContainer, cn.modalContainer)}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Настройки уведомлений
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={onDismiss}>
          <IcoRemove />
        </div>
      </div>
      <div className={classNames(mclasses.formWrapper, cn.form)}>
        {showUserCard && (
          <FieldWrapper label="Пользователь">
            <UserCard className={cn.user} user={form?.user} />
          </FieldWrapper>
        )}
        {loading ? (
          <div className={cn.loading}>
            <Preloader />
          </div>
        ) : availableNotifications.length ? (
          <>
            <FieldWrapper label="Список уведомлений" children={undefined} />
            <div className={cn.list}>
              {availableNotifications.map((notification) => (
                <NotificationSettingsCard
                  key={notification.id}
                  userId={currentUserId}
                  notification={notification}
                />
              ))}
            </div>
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Уведомления не найдены"
          />
        )}
      </div>
      <div className={mclasses.actions}>
        {getBaseRole(form?.user?.role) === 'children' &&
          form?.canChangeParentNotification && (
            <div className={cn.parentNotificationSetting}>
              <CheckboxLabel
                label={
                  'Не отправлять уведомления законному представителю (родителю)'
                }
                value={form?.isParentNotificationEnabled ?? false}
                disabled={parentNotificationLoading}
                onChange={handleParentNotificationChange}
              />
              {parentNotificationLoading && (
                <Spin
                  indicator={
                    <LoadingOutlined
                      spin
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  }
                  size="small"
                />
              )}
            </div>
          )}
        <Button color="gray" onClick={onDismiss}>
          Закрыть
        </Button>
      </div>
    </div>
  )
}

export default NotificationsSettings
